import { useEffect, useRef } from "react";
import { Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { ArrowUpwardRounded, CloseRounded, StopRounded } from "@mui/icons-material";
import VoiceWaveform from "./VoiceWaveform";
import voiceRecordingBarStyles from "./voiceRecordingBarStyles";

type VoiceRecordingBarProps = {
  isTranscribing: boolean;
  canCancel: boolean;
  statusMessage: string;
  arrowWillSend: boolean;
  getAudioLevel: () => number;
  onCancel: () => void;
  onStop: () => void;
  onSend: () => void;
};

export default function VoiceRecordingBar({
  isTranscribing,
  canCancel,
  statusMessage,
  arrowWillSend,
  getAudioLevel,
  onCancel,
  onStop,
  onSend,
}: VoiceRecordingBarProps) {
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const stopButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isTranscribing) {
      stopButtonRef.current?.focus();
      return;
    }

    if (canCancel) {
      cancelButtonRef.current?.focus();
    }
  }, [canCancel, isTranscribing]);

  let cancelLabel = "Aufnahme abbrechen";
  if (!canCancel) {
    cancelLabel = "Senden kann nicht abgebrochen werden";
  } else if (isTranscribing) {
    cancelLabel = "Transkription abbrechen";
  }

  return (
    <Stack direction={"row"} spacing={{ xs: 0.5, sm: 1 }} sx={voiceRecordingBarStyles.bar}>
      <IconButton
        ref={cancelButtonRef}
        onClick={onCancel}
        disabled={!canCancel}
        size={"small"}
        aria-label={cancelLabel}
        sx={voiceRecordingBarStyles.controlButton}
      >
        <CloseRounded />
      </IconButton>
      {isTranscribing ? (
        <Stack
          direction={"row"}
          spacing={{ xs: 0.5, sm: 1 }}
          sx={voiceRecordingBarStyles.content}
          role={"status"}
          aria-live={"polite"}
        >
          <CircularProgress size={18} color={"inherit"} />
          <Typography variant={"body2"} noWrap>
            {statusMessage}
          </Typography>
        </Stack>
      ) : (
        <Box sx={voiceRecordingBarStyles.waveform}>
          <VoiceWaveform getAudioLevel={getAudioLevel} />
        </Box>
      )}
      <IconButton
        ref={stopButtonRef}
        onClick={onStop}
        disabled={isTranscribing}
        size={"small"}
        aria-label={"Aufnahme stoppen"}
        sx={[voiceRecordingBarStyles.controlButton, voiceRecordingBarStyles.stopButton]}
      >
        <StopRounded />
      </IconButton>
      <IconButton
        onClick={onSend}
        disabled={isTranscribing}
        size={"small"}
        aria-label={arrowWillSend ? "Aufnahme transkribieren und senden" : "Aufnahme transkribieren und einfügen"}
        sx={[voiceRecordingBarStyles.controlButton, voiceRecordingBarStyles.sendButton]}
      >
        <ArrowUpwardRounded />
      </IconButton>
    </Stack>
  );
}
